import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getProjectById } from '../../../redux/MainAction';
import { Modal } from 'react-responsive-modal';
import {
  MdAttachMoney,
  MdPeople,
  MdInfo,
  MdPerson,
  MdClose,
} from 'react-icons/md';
import './ProjectDetails.css';

const ProjectDetails = ({ open, onClose, ID }) => {
  const dispatch = useDispatch();
  const projectData = useSelector((state) => state?.Webtracker?.projectByID);

  useEffect(() => {
    if (ID) getProjectDetails();
  }, [ID]);

  const getProjectDetails = () => {
    dispatch(
      getProjectById({
        id: ID,
      })
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status) => {
    const statusClass =
      status === 'ACTIVE' ? 'badge-success' : 'badge-secondary';
    return <span className={`badge ${statusClass}`}>{status}</span>;
  };

  // Members
  const [expandedMemberId, setExpandedMemberId] = useState(null);

  const toggleMember = (id) => {
    setExpandedMemberId((prev) => (prev === id ? null : id));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      showCloseIcon={false}
      classNames={{ modal: 'custom-modal project-details-modal' }}
      center
    >
      <div className="project-details-container">
        <div className="details-header">
          <div className="header-content">
            <h5 className="project-title">
              {projectData?.name || 'Project Details'}
            </h5>
            <div className="header-meta">
              <span className="badge badge-secondary">
                ID: {projectData?.projectId}
              </span>

              {getStatusBadge(projectData?.status)}

              <div className="close-icon" onClick={onClose}>
                <MdClose />
              </div>
            </div>
          </div>
        </div>

        <div className="details-content">
          <div className="section">
            <h6 className="section-title">
              <MdInfo className="section-icon" /> Project Overview
            </h6>
            <div className="info-grid">
              <div className="info-item">
                <label>Billable</label>
                <span className="value">
                  {projectData?.isBillable ? '✓ Yes' : '✗ No'}
                </span>
              </div>
              <div className="info-item">
                <label>Screenshot Mode</label>
                <span className="value">
                  {projectData?.screenshotMode || 'N/A'}
                </span>
              </div>
              <div className="info-item">
                <label>Time Estimate</label>
                <span className="value">
                  {projectData?.timeEstimate || '0'} hrs
                </span>
              </div>
              <div className="info-item">
                <label>Budget Estimate</label>
                <span className="value">
                  ${projectData?.budgetEstimate || '0'}
                </span>
              </div>

              <div className="info-item">
                <label>Start Date</label>
                <span className="value">
                  {formatDate(projectData?.startDate)}
                </span>
              </div>
              <div className="info-item">
                <label>End Date</label>
                <span className="value">
                  {formatDate(projectData?.endDate)}
                </span>
              </div>
            </div>
            {projectData?.projectNotes && (
              <div className="notes-box">
                <strong>Notes:</strong>
                <p>{projectData.projectNotes}</p>
              </div>
            )}
          </div>

          <div className="section">
            <h6 className="section-title">
              <MdAttachMoney className="section-icon" /> Rates & Limits
            </h6>
            <div className="rates-grid">
              <div className="rate-item">
                <label>Hourly Rate</label>
                <span className="value">
                  ${projectData?.hourlyRate || '0'}/hr
                </span>
              </div>
              <div className="rate-item">
                <label>Weekly Limit per Member</label>
                <span className="value">
                  {projectData?.weeklyLimitPerMember || '0'} hrs
                </span>
              </div>
            </div>
          </div>

          {projectData?.projectMembers &&
            projectData.projectMembers.length > 0 && (
              <div className="section">
                <h6 className="section-title">
                  <MdPeople className="section-icon" /> Team Members (
                  {projectData.projectMembers.length})
                </h6>
                <div className="members-list">
                  {projectData.projectMembers.map((member) => {
                    const isExpanded = expandedMemberId === member.id;
                    return (
                      <div
                        key={member.id}
                        className={`member-card ${isExpanded ? 'expanded' : ''}`}
                        style={{ padding: '10px 12px' }}
                      >
                        <div
                          className="member-header"
                          style={{ borderBottom: 'none' }}
                        >
                          <div className="member-info">
                            <MdPerson className="member-icon" />
                            <p className="member-name">
                              {member.employee?.firstName}{' '}
                              {member.employee?.lastName}
                            </p>
                          </div>

                          <div
                            className="d-flex align-items-center"
                            style={{ gap: '0.5rem' }}
                          >
                            {getStatusBadge(member.status)}
                            <button
                              className="btn btn-sm btn-primary"
                              style={{ fontSize: '11px', padding: '2px 6px' }}
                              onClick={() => toggleMember(member.id)}
                            >
                              {isExpanded ? 'Hide' : 'View'}
                            </button>
                          </div>
                        </div>

                        {isExpanded && (
                          <div
                            className="member-details"
                            style={{
                              marginTop: '8px',
                              paddingTop: '8px',
                              borderTop: '1px solid #e2e8f0',
                            }}
                          >
                            <div className="detail-row">
                              <span className="detail-label">
                                Hourly Rate Type:
                              </span>
                              <span className="detail-value">
                                {member.hourlyRateType}
                              </span>
                            </div>

                            {member.hourlyRate && (
                              <div className="detail-row">
                                <span className="detail-label">Rate:</span>
                                <span className="detail-value">
                                  ${member.hourlyRate}/hr
                                </span>
                              </div>
                            )}

                            {member.weeklyLimitPerMember && (
                              <div className="detail-row">
                                <span className="detail-label">
                                  Weekly Limit:
                                </span>
                                <span className="detail-value">
                                  {member.weeklyLimitPerMember} hrs
                                </span>
                              </div>
                            )}

                            <div className="detail-row">
                              <span className="detail-label">Added:</span>
                              <span className="detail-value">
                                {formatDate(member.createdAt)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
        </div>
      </div>
    </Modal>
  );
};

export default ProjectDetails;
